import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Typography, BoxBorder } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import {
  TableContent,
  TableContentItemCaptions,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFixedIncomeAssetContext } from '../../../../../contexts/products/FixedIncome';
import { useFixedIncomeAssetController } from '../../../../../hooks/products/FixedIncome';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemTablet } from '../ListLoadingItem/tablet';
import {
  ApplicationInfo,
  FeeInfo,
  MaturityInfo,
  NetValueInfo,
  ProfitabilityInfo,
  RiskInfo,
  TaxInfo,
  UnitPriceInfo,
} from '../shared';

import { FixedIncomeListItemTypes } from '.';

export const FixedIncomeListItemTablet: React.FC<FixedIncomeListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdNormalized;
  const {
    control,
    currentValue,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    currentValueFormatted,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAssetController(offer);
  const { setModalOpen, setOfferSelected, setOfferFeeSelected } = useFixedIncomeAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = {
    ...offer,
    orderValue: currentValue || 0,
    orderFee: selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  const { Button, isSelected, forceLoading } = infoGetter(
    'fixedIncome',
    completeOffer,
    'productIdNormalized'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
    setOfferFeeSelected(selectedFee);
  };

  if (forceLoading) {
    return <ListLoadingItemTablet />;
  }

  const infosProps = {
    offer: completeOffer,
    selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  return (
    <TableContent direction="row" space={0} p={0} px="inline.xxxs" isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding="inset.sm" space="stack.xxxs" w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <VStack space="stack.quarck">
            <UI.Text variant="semibold">{offer.productDisplayNormalized}</UI.Text>
            <TableContentItemCaptions icons={offer.captions} forceDesktop />
          </VStack>
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon name="Info" size="xs" />
          </Pressable>
        </HStack>
        <HStack justifyContent="space-between">
          <ApplicationInfo w="1/4" {...infosProps} />
          <RiskInfo w="1/4" {...infosProps} />
          <TaxInfo w="1/4" {...infosProps} />
          <ProfitabilityInfo w="1/4" {...infosProps} />
        </HStack>
        <HStack justifyContent="space-between">
          <MaturityInfo w="1/4" {...infosProps} />
          <UnitPriceInfo w="1/4" {...infosProps} />
          <FeeInfo w="1/4" {...infosProps} />
          <NetValueInfo w="1/4" {...infosProps} />
        </HStack>
        <HStack justifyContent="space-between" w="full">
          <VStack space="stack.quarck" w="45%">
            <HStack space="inline.quarck" alignItems="center">
              <UI.Text variant="semibold">Taxa</UI.Text>
              <UI.Text fontSize="xs">(Spread)</UI.Text>
            </HStack>
            <Stack mr="inline.xxxs" bgColor="backgroundshade" borderRadius={BoxBorder.thick}>
              <UI.Select
                triggerProps={{
                  isDisabled: !ableToChangeFees,
                  _disabled: { opacity: 0.2 },
                }}
                testID="fee-select"
                options={offer.distributionFeesNormalized.map((feeOption) => ({
                  label: feeOption.label,
                  value: feeOption.feeId.toString(),
                }))}
                disabled={isSelected}
                placeholder="--"
                selectedValue={selectedFeeId?.toString() || ''}
                onValueChange={(value) => setSelectedFeeId(Number(value))}
              />
            </Stack>
          </VStack>
          <HStack w="2/4" space="inline.xxxs">
            <VStack space="stack.quarck" flex={1}>
              <UI.Text variant="semibold">Valor a aplicar</UI.Text>
              <VStack flex={1}>
                <InputMoney
                  id={`fixedIncome-input${id}`}
                  name="createAppliedAmount"
                  control={control}
                  handleFocus={handleInputFocus}
                  handleBlur={handleInputBlur}
                  handleSubmit={handleSubmit}
                  handleClear={handleInputClear}
                  isDisabled={isSelected}
                  currentValue={currentValueFormatted}
                  hasFocus={isFocused}
                  minValue={minApplication}
                  customInputProps={{
                    ...{
                      ...(showMessageAdjustUnitPrice && {
                        helpTextProps,
                        helpText,
                      }),
                    },
                    ...{ ...(isFocused && hasError && { error: hasError }) },
                    ...{ ...(isFocused && !hasError && { helpText }) },
                  }}
                />
                {showMessageAdjustUnitPrice && !isFocused && (
                  <TableContentItemText fontSize={Typography['fontsize.xs']} color="neutral.light">
                    Ajustado por preço unitário
                  </TableContentItemText>
                )}
              </VStack>
            </VStack>
            <Stack justifyContent="flex-end">
              <Button
                id={selectedTab.typeGetterId}
                offer={completeOffer}
                isDisabled={typeof hasError?.message === 'string'}
                productContextConstant={selectedTab}
              />
            </Stack>
          </HStack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
